import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Bot,
  CheckCircle,
  Check,
  Copy,
  DollarSign,
  Eye,
  Facebook,
  FileText,
  Globe,
  KeyRound,
  Link2,
  Megaphone,
  MousePointer,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Funnel,
  FunnelChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { GlassCard, GlassButton, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';
import SocialApproval from './SocialApproval';

const tabs = [
  { id: 'social', label: 'Aprovação social', icon: ShieldCheck },
  { id: 'bi', label: 'BI do Grupo', icon: BarChart3 },
  { id: 'atencao', label: 'Atenção', icon: Bot },
  { id: 'newsletter', label: 'Newsletter', icon: Send },
  { id: 'campanhas', label: 'Campanhas', icon: Megaphone },
  { id: 'leads', label: 'Leads e funil', icon: Users },
  { id: 'conectores', label: 'Conectores', icon: Globe },
  { id: 'credenciais', label: 'Credenciais Ads', icon: KeyRound },
  { id: 'blog', label: 'Conteúdo', icon: FileText },
];

const articleCategories = [
  'Seções', 'Redes Sociais', 'Novos Empreendimentos', 'Notícias',
  'Investimento', 'Imóvel', 'Imóveis a Venda', 'Imobiliário',
  'Imobiliária', 'Financiamento Imobiliário', 'Diversos', 'Dicas',
  'Destaque', 'Decoração', 'Praia Brava', 'Navegantes',
  'Balneário Piçarras', 'Balneário Camboriú', 'Cidades Atendidas',
  'Curiosidades',
];

const editorialCities = [
  ['Balneário Camboriú', ['balneario camboriu', ' bc ']],
  ['Balneário Piçarras', ['balneario picarras', 'picarras']],
  ['Porto Belo', ['porto belo']],
  ['Praia Brava', ['praia brava']],
  ['Navegantes', ['navegantes']],
  ['Bombinhas', ['bombinhas']],
  ['Itajaí', ['itajai']],
  ['Camboriú', ['camboriu']],
  ['Penha', ['penha']],
];

const normalizeEditorialText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const citiesForPost = (post) => {
  const haystack = ` ${normalizeEditorialText([post.titulo, post.topic, post.categoria, post.keyword_target, post.blog_keywords].filter(Boolean).join(' '))} `;
  const cities = editorialCities
    .filter(([, aliases]) => aliases.some((alias) => haystack.includes(alias)))
    .map(([city]) => city);
  return cities.filter((city) => !(city === 'Camboriú' && cities.includes('Balneário Camboriú')));
};

const labelPostStatus = (status) => ({
  pending: 'Pendente',
  approved: 'Aprovado',
  discarded: 'Descartado',
  review: 'Em revisão',
  needs_review: 'Revisão necessária',
}[status] || status || 'Sem status');

const localDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createNewsletterForm = () => {
  const now = new Date();
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const saturday = new Date(monday);
  saturday.setDate(monday.getDate() + 5);
  return {
    data_inicio: localDateValue(monday),
    data_fim: localDateValue(sunday),
    send_at: `${localDateValue(saturday)}T09:00`,
    subject: '',
    theme_ids: [],
    news_items: Array.from({ length: 2 }, () => ({ title: '', summary: '', source: '', url: '' })),
    investor_metrics: Array.from({ length: 2 }, () => ({ value: '', label: '', source: '', url: '' })),
    research: {},
    properties: Array.from({ length: 2 }, () => ({
      code: '',
      title: '',
      price: '',
      details: '',
      badge: '',
      image_url: '',
      url: '',
      whatsapp_url: '',
    })),
    brand: {},
  };
};

const createOpportunityForm = () => ({
  campaign_id: '',
  source_url: '',
  opportunity_type: 'pronto',
  advantage: '',
  valid_until: '',
  whatsapp_url: '',
  property: { source_url: '', url: '', title: '', description: '', image_url: '', price: '', details: '' },
  subject: '',
  preheader: '',
  heading: '',
  body: '',
  brand: {},
});

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const number = (value) => Number(value || 0).toLocaleString('pt-BR');

const percent = (value) => `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

const chartColors = ['#2563eb', '#0f766e', '#d97706', '#7c3aed', '#dc2626', '#0284c7', '#16a34a', '#9333ea'];

const shortLabel = (value, max = 18) => {
  const text = String(value || 'Nao informado');
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-900/95 p-3 text-sm shadow-glass">
      {label && <p className="mb-2 font-medium text-white/90">{label}</p>}
      {payload.map((entry) => (
        <div key={`${entry.name}-${entry.dataKey}`} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-white/60">{entry.name}:</span>
          <span className="font-medium text-white/90">
            {typeof entry.value === 'number' ? number(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const ChartFrame = ({ title, subtitle, children }) => (
  <GlassCard className="p-5" hover={false}>
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-white/90">{title}</h3>
      {subtitle && <p className="text-sm text-white/45">{subtitle}</p>}
    </div>
    {children}
  </GlassCard>
);

const SimpleBarList = ({ data, labelKey, valueKey, valueFormatter = number, color = '#2563eb' }) => {
  const max = Math.max(...data.map((item) => Number(item[valueKey] || 0)), 1);
  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const value = Number(item[valueKey] || 0);
        return (
          <div key={`${item[labelKey]}-${index}`}>
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="truncate text-white/75">{item[labelKey] || 'Nao informado'}</span>
              <span className="font-medium text-white/90">{valueFormatter(value)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.max((value / max) * 100, value ? 8 : 0)}%`, backgroundColor: chartColors[index % chartColors.length] || color }}
              />
            </div>
          </div>
        );
      })}
      {!data.length && <p className="text-sm text-white/45">Sem dados para este gráfico.</p>}
    </div>
  );
};

const statusClass = {
  atualizado: 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200',
  cadastro_erp: 'border-blue-400/25 bg-blue-500/10 text-blue-200',
  aguardando_importacao: 'border-amber-400/25 bg-amber-500/10 text-amber-100',
  configuracao_pendente: 'border-amber-400/25 bg-amber-500/10 text-amber-100',
  ativo: 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200',
  configurado: 'border-cyan-400/25 bg-cyan-500/10 text-cyan-200',
  planejado: 'border-amber-400/25 bg-amber-500/10 text-amber-100',
  erro: 'border-red-400/25 bg-red-500/10 text-red-100',
  alto: 'border-red-400/25 bg-red-500/10 text-red-100',
  medio: 'border-amber-400/25 bg-amber-500/10 text-amber-100',
  baixo: 'border-cyan-400/25 bg-cyan-500/10 text-cyan-100',
};

const toneClass = {
  emerald: 'border-emerald-300/20 bg-emerald-400/10 text-emerald-200',
  cyan: 'border-cyan-300/20 bg-cyan-400/10 text-cyan-200',
  amber: 'border-amber-300/20 bg-amber-400/10 text-amber-200',
  blue: 'border-blue-300/20 bg-blue-400/10 text-blue-200',
};

const connectorIcon = {
  meta_ads: Facebook,
  google_ads: MousePointer,
  rd_station: Send,
  vista_loft: CheckCircle,
  superlogica: DollarSign,
  portais_scraping: Globe,
  evolution_whatsapp: Bot,
};

const KpiCard = ({ title, value, subtitle, icon: Icon, tone = 'cyan' }) => (
  <GlassCard className="p-5" hover={false}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm text-white/50">{title}</p>
        <p className="mt-2 text-2xl font-semibold text-white/90">{value}</p>
        {subtitle && <p className="mt-1 text-xs text-white/40">{subtitle}</p>}
      </div>
      <div className={`rounded-xl border p-3 ${toneClass[tone] || toneClass.cyan}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </GlassCard>
);

const EmptyState = ({ children }) => (
  <GlassCard className="p-8 text-center" hover={false}>
    <p className="text-sm text-white/45">{children}</p>
  </GlassCard>
);

const EmptyChart = ({ children }) => (
  <div className="flex h-64 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] px-6 text-center">
    <p className="text-sm text-white/45">{children}</p>
  </div>
);

const SocialBlock = ({ title, subtitle, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
    <div className="mb-3">
      <h5 className="font-semibold text-white/90">{title}</h5>
      {subtitle && <p className="text-xs text-white/40">{subtitle}</p>}
    </div>
    <div className="whitespace-pre-wrap text-sm leading-6 text-white/75">{children}</div>
  </div>
);

export const Marketing = () => {
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'social' ? 'social' : 'bi');
  const [socialPackageToOpen, setSocialPackageToOpen] = useState(() => new URLSearchParams(window.location.search).get('package'));
  const [socialDirty, setSocialDirty] = useState(false);
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postRelationships, setPostRelationships] = useState(null);
  const [contentFilters, setContentFilters] = useState({
    query: '', status: 'all', city: 'all', category: 'all', collection: 'all', sort: 'id_asc',
  });
  const [postHtml, setPostHtml] = useState('');
  const [postView, setPostView] = useState('preview');
  const [savingPost, setSavingPost] = useState(false);
  const [postMetadata, setPostMetadata] = useState({});
  const [revisionRequest, setRevisionRequest] = useState('');
  const [revisingPost, setRevisingPost] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncingRd, setSyncingRd] = useState(false);
  const [busyConnector, setBusyConnector] = useState('');
  const [credentialsStatus, setCredentialsStatus] = useState(null);
  const [credentialForms, setCredentialForms] = useState({});
  const [savingCredentials, setSavingCredentials] = useState('');
  const [copiedField, setCopiedField] = useState('');
  const [publishedValidation, setPublishedValidation] = useState(null);
  const [creatingSocialForArticleId, setCreatingSocialForArticleId] = useState(null);
  const [validatingPublished, setValidatingPublished] = useState(false);
  const [newsletterData, setNewsletterData] = useState(null);
  const [newsletterForm, setNewsletterForm] = useState(() => createNewsletterForm());
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSaving, setNewsletterSaving] = useState('');
  const [newsletterPropertyExtracting, setNewsletterPropertyExtracting] = useState(null);
  const [newsletterMode, setNewsletterMode] = useState('weekly');
  const [opportunityForm, setOpportunityForm] = useState(() => createOpportunityForm());
  const [opportunityData, setOpportunityData] = useState(null);
  const [opportunityPreview, setOpportunityPreview] = useState(null);
  const [opportunityLoading, setOpportunityLoading] = useState(false);
  const [opportunitySaving, setOpportunitySaving] = useState('');
  const [error, setError] = useState('');
  const [socialSuccess, setSocialSuccess] = useState('');
  const [socialPackages, setSocialPackages] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [biRes, postsRes, credentialsRes, socialRes] = await Promise.all([
        api.get('/marketing/bi/resumo'),
        api.get('/marketing/blog/posts', { params: { per_page: 500 } }),
        api.get('/marketing/ads/credentials/status'),
        api.get('/marketing/social/packages'),
      ]);
      setData(biRes.data || null);
      setPosts(postsRes.data?.items || []);
      setCredentialsStatus(credentialsRes.data || null);
      setSocialPackages(socialRes.data?.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao carregar BI de marketing.');
    } finally {
      setLoading(false);
    }
  }, []);

  const openPost = async (post) => {
    setError('');
    setPostRelationships(null);
    try {
      const [response, relationshipsResponse] = await Promise.all([
        api.get(`/marketing/blog/posts/${post.id}`),
        api.get(`/marketing/blog/posts/${post.id}/relationships`),
      ]);
      setSelectedPost(response.data);
      setPostRelationships(relationshipsResponse.data || null);
      setPostHtml(response.data?.blog_html || '');
      setPostMetadata({
        titulo: response.data?.titulo || response.data?.topic || '',
        slug: response.data?.slug || '',
        blog_meta: response.data?.blog_meta || '',
        blog_keywords: response.data?.blog_keywords || '',
        resumo: response.data?.resumo || '',
        categoria: response.data?.categoria || '',
        publication_date: response.data?.publication_date || '',
        instagram_caption: response.data?.instagram_caption || '',
        instagram_hashtags: response.data?.instagram_hashtags || '',
        tiktok_script: response.data?.tiktok_script || '',
        facebook_post: response.data?.facebook_post || '',
        published_url: response.data?.published_url || '',
      });
      setPublishedValidation(
        response.data?.published_validation_status
          ? {
              status: response.data.published_validation_status,
              similarity_percent: response.data.published_similarity,
              validated_at: response.data.published_validated_at,
            }
          : null
      );
      setRevisionRequest('');
      setPostView('preview');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao abrir o artigo.');
    }
  };

  const contentFacets = useMemo(() => ({
    cities: [...new Set(posts.flatMap(citiesForPost))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    categories: [...new Set(posts.map((post) => post.categoria).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    collections: [...new Set(posts.map((post) => post.hub_work_id).filter(Boolean))].sort(),
  }), [posts]);

  const filteredPosts = useMemo(() => {
    const query = normalizeEditorialText(contentFilters.query).trim();
    const filtered = posts.filter((post) => {
      const haystack = normalizeEditorialText([
        post.id, post.titulo, post.topic, post.categoria, post.keyword_target, post.blog_keywords, post.hub_work_id,
      ].filter(Boolean).join(' '));
      return (!query || haystack.includes(query))
        && (contentFilters.status === 'all' || post.status === contentFilters.status)
        && (contentFilters.city === 'all' || citiesForPost(post).includes(contentFilters.city))
        && (contentFilters.category === 'all' || post.categoria === contentFilters.category)
        && (contentFilters.collection === 'all'
          || (contentFilters.collection === 'without_collection' ? !post.hub_work_id : post.hub_work_id === contentFilters.collection));
    });
    return filtered.sort((a, b) => {
      if (contentFilters.sort === 'id_desc') return b.id - a.id;
      if (contentFilters.sort === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (contentFilters.sort === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      return a.id - b.id;
    });
  }, [posts, contentFilters]);

  const updateContentFilter = (key, value) => setContentFilters((current) => ({ ...current, [key]: value }));

  const saveMetadata = async () => {
    await updatePost(postMetadata);
  };

  const copyPublicationField = async (key, value) => {
    const text = String(value || '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (clipboardError) {
      const helper = document.createElement('textarea');
      helper.value = text;
      helper.setAttribute('readonly', '');
      helper.style.position = 'fixed';
      helper.style.opacity = '0';
      document.body.appendChild(helper);
      helper.select();
      document.execCommand('copy');
      document.body.removeChild(helper);
    }
    setCopiedField(key);
    window.setTimeout(() => {
      setCopiedField((current) => (current === key ? '' : current));
    }, 1800);
  };

  const validatePublishedArticle = async () => {
    if (!selectedPost || !postMetadata.published_url?.trim()) {
      setError('Cole o link da versão publicada antes de conferir.');
      return;
    }
    setValidatingPublished(true);
    setError('');
    try {
      const response = await api.post(`/marketing/blog/posts/${selectedPost.id}/validate-published`, {
        url: postMetadata.published_url.trim(),
      });
      setPublishedValidation(response.data?.validation || null);
      setSelectedPost(response.data?.post || selectedPost);
      setPosts((current) => current.map((item) => (
        item.id === selectedPost.id ? { ...item, ...(response.data?.post || {}) } : item
      )));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao conferir a versão publicada.');
    } finally {
      setValidatingPublished(false);
    }
  };

  const socialPackageFor = (articleId) => socialPackages.find((item) => Number(item.payload?.article_id) === Number(articleId));
  const openSocialPackage = (packageId) => {
    const next = new URL(window.location.href); next.searchParams.set('tab', 'social'); next.searchParams.set('package', packageId); window.history.pushState({}, '', next);
    setSocialPackageToOpen(packageId); setSelectedPost(null); setActiveTab('social');
  };

  const generateSocialPackageFromArticle = async (post = selectedPost) => {
    if (!post?.id) return;
    setCreatingSocialForArticleId(post.id);
    setError('');
    setSocialSuccess('');
    try {
      const { data } = await api.post('/marketing/social/packages/from-article', { article_id: post.id });
      const next = new URL(window.location.href); next.searchParams.set('tab', 'social'); next.searchParams.set('package', data.id); window.history.pushState({}, '', next);
      setSocialPackages((items) => [data, ...items.filter((item) => item.id !== data.id)]);
      setSocialPackageToOpen(data.id);
      setSocialSuccess(`Pacote social do artigo ${post.id} criado com sucesso. Abrindo a revisão.`);
      setSelectedPost(null);
      setActiveTab('social');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Não foi possível preparar o pacote social.');
    } finally {
      setCreatingSocialForArticleId(null);
    }
  };

  const requestRevision = async () => {
    if (!selectedPost || !revisionRequest.trim()) return;
    setRevisingPost(true);
    setError('');
    try {
      const response = await api.post(`/marketing/blog/posts/${selectedPost.id}/chat`, {
        mensagem: revisionRequest.trim(),
      });
      const updatedHtml = response.data?.artigo_atualizado || '';
      if (!updatedHtml) throw new Error('O Hub não retornou o artigo revisado.');
      setPostHtml(updatedHtml);
      setSelectedPost((current) => ({ ...current, blog_html: updatedHtml, status: 'needs_review' }));
      setPosts((current) => current.map((item) => (
        item.id === selectedPost.id ? { ...item, status: 'needs_review' } : item
      )));
      setRevisionRequest('');
      setPostView('preview');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao solicitar alteração.');
    } finally {
      setRevisingPost(false);
    }
  };

  const updatePost = async (changes, closeAfter = false) => {
    if (!selectedPost) return;
    setSavingPost(true);
    setError('');
    try {
      const response = await api.patch(`/marketing/blog/posts/${selectedPost.id}`, changes);
      setSelectedPost(response.data);
      setPostHtml(response.data?.blog_html || postHtml);
      setPosts((current) => current.map((item) => (item.id === response.data.id ? response.data : item)));
      if (closeAfter) setSelectedPost(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao salvar o artigo.');
    } finally {
      setSavingPost(false);
    }
  };

  const loadNewsletter = useCallback(async (periodOverride = null) => {
    const period = periodOverride || {
      data_inicio: newsletterForm.data_inicio,
      data_fim: newsletterForm.data_fim,
    };
    setNewsletterLoading(true);
    setError('');
    try {
      const response = await api.get('/marketing/newsletter/weekly', { params: period });
      const payload = response.data || {};
      const saved = payload.existing_draft;
      const metadata = saved?.metadata || {};
      const eligibleThemes = payload.eligible_themes || [];
      const legacyArticleIds = new Set(metadata.article_ids || []);
      const savedThemeIds = metadata.theme_ids?.length
        ? metadata.theme_ids
        : eligibleThemes
          .filter((theme) => (theme.article_ids || []).some((id) => legacyArticleIds.has(id)))
          .map((theme) => theme.id);
      setNewsletterData(payload);
      setNewsletterForm((current) => ({
        ...current,
        data_inicio: payload.period?.data_inicio || current.data_inicio,
        data_fim: payload.period?.data_fim || current.data_fim,
        send_at: metadata.send_at || current.send_at,
        subject: saved?.subject || payload.default_subject || current.subject,
        theme_ids: savedThemeIds.length ? savedThemeIds : (payload.recommended_theme_ids || []),
        news_items: metadata.news_items?.length
          ? metadata.news_items
          : createNewsletterForm().news_items,
        investor_metrics: metadata.investor_metrics?.length
          ? metadata.investor_metrics
          : createNewsletterForm().investor_metrics,
        research: metadata.research || {},
        properties: metadata.properties?.length
          ? Array.from({ length: 2 }, (_, index) => metadata.properties[index] || createNewsletterForm().properties[index])
          : current.properties,
        brand: metadata.brand || payload.brand_defaults || current.brand,
      }));
      setNewsletterPreview(saved?.html ? {
        html: saved.html,
        subject: saved.subject,
        metadata,
        checks: {
          articles: metadata.articles?.length || 0,
          news_items: metadata.news_items?.length || 0,
          investor_metrics: metadata.investor_metrics?.length || 0,
          properties: metadata.properties?.length || 0,
          ready_for_rd_station: saved.status === 'pronta_rd',
        },
      } : null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao preparar a newsletter semanal.');
    } finally {
      setNewsletterLoading(false);
    }
  }, [newsletterForm.data_inicio, newsletterForm.data_fim]);

  const updateNewsletterListItem = (listName, index, field, value) => {
    setNewsletterForm((current) => ({
      ...current,
      [listName]: current[listName].map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      )),
    }));
  };

  const completeWeeklyPropertyFromLink = async (index) => {
    const property = newsletterForm.properties[index] || {};
    if (!property.url?.trim()) {
      setError(`Informe o link de detalhes do imóvel ${index + 1} antes de completar os dados.`);
      return;
    }
    setNewsletterPropertyExtracting(index);
    setError('');
    try {
      const response = await api.post('/marketing/newsletter/opportunity/extract', { source_url: property.url });
      const extracted = response.data?.property || {};
      setNewsletterForm((current) => ({
        ...current,
        properties: current.properties.map((item, itemIndex) => {
          if (itemIndex !== index) return item;
          return {
            ...item,
            title: item.title?.trim() || extracted.title || '',
            price: item.price?.trim() || extracted.price || '',
            details: item.details?.trim() || extracted.details || '',
            image_url: item.image_url?.trim() || extracted.image_url || '',
            url: item.url?.trim() || extracted.source_url || '',
          };
        }),
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || `Não foi possível completar os dados do imóvel ${index + 1}.`);
    } finally {
      setNewsletterPropertyExtracting(null);
    }
  };

  const toggleNewsletterTheme = (themeId) => {
    if (newsletterForm.theme_ids.includes(themeId)) {
      setNewsletterForm((current) => ({
        ...current,
        theme_ids: current.theme_ids.filter((id) => id !== themeId),
      }));
      return;
    }
    const maxThemes = newsletterData?.editorial_limits?.max_themes || 6;
    if (newsletterForm.theme_ids.length >= maxThemes) {
      setError(`A newsletter aceita no máximo ${maxThemes} temas. Retire um tema antes de adicionar outro.`);
      return;
    }
    setError('');
    setNewsletterForm((current) => ({
      ...current,
      theme_ids: [...current.theme_ids, themeId],
    }));
  };

  const moveNewsletterTheme = (themeId, direction) => {
    setNewsletterForm((current) => {
      const index = current.theme_ids.indexOf(themeId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.theme_ids.length) return current;
      const nextIds = [...current.theme_ids];
      [nextIds[index], nextIds[target]] = [nextIds[target], nextIds[index]];
      return { ...current, theme_ids: nextIds };
    });
  };

  const newsletterRequestPayload = () => ({
    ...newsletterForm,
    news_items: newsletterForm.news_items.filter((item) => item.title?.trim()),
    investor_metrics: newsletterForm.investor_metrics.filter((item) => item.value?.trim() && item.label?.trim()),
    properties: newsletterForm.properties.filter((item) => item.title?.trim()),
  });

  const generateNewsletterPreview = async () => {
    setNewsletterSaving('preview');
    setError('');
    try {
      const response = await api.post('/marketing/newsletter/weekly/preview', {
        ...newsletterRequestPayload(),
        refresh_research: true,
      });
      setNewsletterPreview(response.data || null);
      const metadata = response.data?.metadata || {};
      setNewsletterForm((current) => ({
        ...current,
        subject: response.data?.subject || current.subject,
        news_items: metadata.news_items || [],
        investor_metrics: metadata.investor_metrics || [],
        research: metadata.research || {},
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao gerar a prévia da newsletter.');
    } finally {
      setNewsletterSaving('');
    }
  };

  const saveNewsletter = async (status) => {
    setNewsletterSaving(status);
    setError('');
    try {
      const response = await api.post('/marketing/newsletter/weekly/save', {
        ...newsletterRequestPayload(),
        status,
        refresh_research: false,
      });
      const campaign = response.data?.campaign;
      setNewsletterPreview(campaign?.html ? {
        html: campaign.html,
        subject: campaign.subject,
        metadata: campaign.metadata,
        checks: response.data?.checks,
      } : newsletterPreview);
      await loadNewsletter({
        data_inicio: newsletterForm.data_inicio,
        data_fim: newsletterForm.data_fim,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao salvar a newsletter.');
    } finally {
      setNewsletterSaving('');
    }
  };

  const loadOpportunityCampaigns = useCallback(async () => {
    setOpportunityLoading(true);
    setError('');
    try {
      const response = await api.get('/marketing/newsletter/opportunity');
      setOpportunityData(response.data || {});
      setOpportunityForm((current) => ({
        ...current,
        brand: current.brand && Object.keys(current.brand).length ? current.brand : (response.data?.brand_defaults || {}),
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar as oportunidades especiais.');
    } finally {
      setOpportunityLoading(false);
    }
  }, []);

  const updateOpportunity = (field, value) => setOpportunityForm((current) => ({ ...current, [field]: value }));
  const updateOpportunityProperty = (field, value) => setOpportunityForm((current) => ({
    ...current,
    property: { ...current.property, [field]: value },
  }));

  const extractOpportunity = async () => {
    if (!opportunityForm.source_url.trim()) {
      setError('Cole o link público do imóvel antes de preparar a campanha.');
      return;
    }
    setOpportunitySaving('extract');
    setError('');
    try {
      const response = await api.post('/marketing/newsletter/opportunity/extract', { source_url: opportunityForm.source_url });
      const property = response.data?.property || {};
      setOpportunityForm((current) => ({
        ...current,
        source_url: property.source_url || current.source_url,
        property: { ...current.property, ...property, url: property.source_url || current.property.url },
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Não foi possível ler o link do imóvel.');
    } finally {
      setOpportunitySaving('');
    }
  };

  const opportunityPayload = () => ({ ...opportunityForm, property: { ...opportunityForm.property, source_url: opportunityForm.source_url || opportunityForm.property.source_url } });

  const generateOpportunityPreview = async () => {
    setOpportunitySaving('preview');
    setError('');
    try {
      const response = await api.post('/marketing/newsletter/opportunity/preview', opportunityPayload());
      const payload = response.data || {};
      const copy = payload.metadata?.copy || {};
      setOpportunityPreview(payload);
      setOpportunityForm((current) => ({
        ...current,
        subject: payload.subject || current.subject,
        preheader: copy.preheader || current.preheader,
        heading: copy.heading || current.heading,
        body: copy.body || current.body,
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao gerar a prévia da oportunidade.');
    } finally {
      setOpportunitySaving('');
    }
  };

  const saveOpportunity = async (status) => {
    setOpportunitySaving(status);
    setError('');
    try {
      const response = await api.post('/marketing/newsletter/opportunity/save', { ...opportunityPayload(), status });
      const campaign = response.data?.campaign;
      setOpportunityPreview(campaign?.html ? { html: campaign.html, subject: campaign.subject, metadata: campaign.metadata, checks: response.data?.checks } : opportunityPreview);
      setOpportunityForm((current) => ({ ...current, campaign_id: campaign?.id || current.campaign_id }));
      await loadOpportunityCampaigns();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao salvar a oportunidade especial.');
    } finally {
      setOpportunitySaving('');
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'newsletter' && !newsletterData && !newsletterLoading) {
      loadNewsletter();
    }
  }, [activeTab, loadNewsletter, newsletterData, newsletterLoading]);

  useEffect(() => {
    if (activeTab === 'newsletter' && newsletterMode === 'opportunity' && !opportunityData && !opportunityLoading) {
      loadOpportunityCampaigns();
    }
  }, [activeTab, newsletterMode, opportunityData, opportunityLoading, loadOpportunityCampaigns]);

  const syncRdStation = async () => {
    setSyncingRd(true);
    setError('');
    try {
      await api.post('/marketing/rd/sync', {});
      await loadData();
      setActiveTab('atencao');
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Erro ao sincronizar RD Station.');
      await loadData();
    } finally {
      setSyncingRd(false);
    }
  };

  const runAdsConnector = async (canal, action) => {
    setBusyConnector(`${canal}:${action}`);
    setError('');
    try {
      await api.post(`/marketing/ads/${canal}/${action}`, {});
      await loadData();
      setActiveTab('conectores');
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || err.response?.data?.error || err.message || 'Erro ao processar conector.');
      await loadData();
    } finally {
      setBusyConnector('');
    }
  };

  const updateCredentialField = (canal, key, value) => {
    setCredentialForms((current) => ({
      ...current,
      [canal]: {
        ...(current[canal] || {}),
        [key]: value,
      },
    }));
  };

  const saveCredentials = async (canal) => {
    const values = credentialForms[canal] || {};
    const filledValues = Object.fromEntries(
      Object.entries(values).filter(([, value]) => String(value || '').trim() !== '')
    );
    if (!Object.keys(filledValues).length) {
      setError('Preencha pelo menos um campo para salvar as credenciais.');
      return;
    }
    setSavingCredentials(canal);
    setError('');
    try {
      const res = await api.put('/marketing/ads/credentials', { canal, values: filledValues });
      setCredentialsStatus(res.data?.status || null);
      setCredentialForms((current) => ({ ...current, [canal]: {} }));
      await loadData();
      setActiveTab('credenciais');
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || err.response?.data?.error || err.message || 'Erro ao salvar credenciais.');
    } finally {
      setSavingCredentials('');
    }
  };

  const kpis = data?.kpis || {};
  const attentionItems = data?.attention?.items || [];
  const campanhas = data?.campanhas || [];
  const conectores = data?.conectores || [];
  const periodo = data?.periodo;
  const coberturaDados = data?.cobertura_dados || [];
  const dadosGeradosEm = data?.generated_at;
  const postHasUnsavedChanges = Boolean(selectedPost && (
    postHtml !== (selectedPost.blog_html || '')
    || Object.entries(postMetadata).some(([key, value]) => String(value || '') !== String(selectedPost[key] || ''))
  ));
  const closePost = () => {
    if (savingPost) return;
    if (postHasUnsavedChanges && !window.confirm('Há alterações que ainda não foram salvas. Deseja sair e descartá-las?')) return;
    setSelectedPost(null);
  };

  const topCanais = useMemo(() => (data?.canais || []).slice(0, 8), [data]);
  const channelChart = useMemo(
    () => topCanais.map((item) => ({
      canal: shortLabel(item.canal),
      leads: Number(item.leads || 0),
      qualificados: Number(item.leads_qualificados || 0),
      investimento: Number(item.investimento || 0),
      cliques: Number(item.cliques || 0),
    })),
    [topCanais]
  );
  const funnelChart = useMemo(
    () => {
      const source = data?.kpis || {};
      return [
        { name: 'Leads totais', value: Number(source.leads_total || 0), fill: '#2563eb' },
        { name: 'Quentes/qualificados', value: Number(source.leads_quentes || 0), fill: '#0f766e' },
        { name: 'RD Station', value: Number(source.enviados_rd || 0), fill: '#d97706' },
        { name: 'Vista/Loft', value: Number(source.enviados_vista_loft || 0), fill: '#7c3aed' },
        { name: 'Conversoes Ads', value: Number(source.conversoes || 0), fill: '#dc2626' },
      ].filter((item) => item.value > 0);
    },
    [data]
  );
  const mercadoChart = useMemo(
    () => (data?.mercado_por_finalidade || []).map((item, index) => ({
      name: item.finalidade || 'Nao informado',
      value: Number(item.total || 0),
      fill: chartColors[index % chartColors.length],
    })),
    [data]
  );
  const connectorChart = useMemo(
    () => Object.entries(data?.conectores_status || {}).map(([status, total], index) => ({
      name: status,
      value: Number(total || 0),
      fill: chartColors[index % chartColors.length],
    })),
    [data]
  );

  const renderBI = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard title="Investimento" value={currency(kpis.investimento)} subtitle="Meta/Google e canais pagos importados" icon={DollarSign} tone="emerald" />
        <KpiCard title="Leads totais" value={number(kpis.leads_total)} subtitle={`${number(kpis.leads_quentes)} quentes/qualificados`} icon={Users} tone="cyan" />
        <KpiCard title="CPL médio" value={currency(kpis.cpl)} subtitle={`CTR ${percent(kpis.ctr)} · ${number(kpis.cliques)} cliques`} icon={MousePointer} tone="amber" />
        <KpiCard title="Portais monitorados" value={number(kpis.anuncios_mercado)} subtitle={`${number(kpis.anuncios_venda)} venda · ${number(kpis.anuncios_aluguel)} aluguel`} icon={Globe} tone="blue" />
      </div>

      <GlassCard hover={false}>
        <div className="flex flex-col gap-3 border-b border-white/10 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white/90">Cobertura e procedência dos dados</h3>
            <p className="text-sm text-white/45">Cada indicador abaixo informa se vem do cadastro do ERP ou de uma integração — sem preencher lacunas com estimativas.</p>
          </div>
          {dadosGeradosEm && <span className="text-xs text-white/35">Consulta gerada em {new Date(dadosGeradosEm).toLocaleString('pt-BR')}</span>}
        </div>
        <div className="grid grid-cols-1 divide-y divide-white/10 md:grid-cols-2 md:divide-x md:divide-y-0 xl:grid-cols-3">
          {coberturaDados.map((fonte) => (
            <div key={fonte.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-white/90">{fonte.label}</p>
                <span className={`rounded-full border px-2 py-0.5 text-xs ${statusClass[fonte.status] || 'border-white/10 bg-white/[0.04] text-white/60'}`}>
                  {String(fonte.status || '').replaceAll('_', ' ')}
                </span>
              </div>
              <p className="mt-2 text-sm text-white/65">{number(fonte.rows)} registro(s) no período</p>
              <p className="mt-1 text-xs text-white/40">{fonte.detail}</p>
              {fonte.updated_at && <p className="mt-2 text-xs text-white/35">Última sincronização: {new Date(fonte.updated_at).toLocaleString('pt-BR')}</p>}
              {fonte.error && <p className="mt-2 text-xs text-red-200/80">{fonte.error}</p>}
            </div>
          ))}
          {!coberturaDados.length && <p className="p-5 text-sm text-white/45">A cobertura será exibida ao carregar os dados do BI.</p>}
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <GlassCard className="xl:col-span-2">
          <div className="border-b border-white/10 p-5">
            <h3 className="text-lg font-semibold text-white/90">Canais e origem</h3>
            <p className="text-sm text-white/45">Leads internos já aparecem aqui; investimento entra quando Meta/Google forem sincronizados.</p>
          </div>
          <div className="divide-y divide-white/10">
            {topCanais.map((item) => (
              <div key={item.canal} className="grid grid-cols-2 gap-3 p-4 text-sm md:grid-cols-6">
                <div className="col-span-2">
                  <p className="font-medium text-white/90">{item.canal}</p>
                  <p className="text-xs text-white/40">CPL {currency(item.cpl)} · CTR {percent(item.ctr)}</p>
                </div>
                <span className="text-white/65">{number(item.leads)} leads</span>
                <span className="text-white/65">{number(item.leads_qualificados)} qualificados</span>
                <span className="text-white/65">{number(item.cliques)} cliques</span>
                <span className="text-white/65">{currency(item.investimento)}</span>
              </div>
            ))}
            {!topCanais.length && <p className="p-5 text-sm text-white/45">Sem canais no período.</p>}
          </div>
        </GlassCard>

        <GlassCard>
          <div className="border-b border-white/10 p-5">
            <h3 className="text-lg font-semibold text-white/90">Leitura do Copiloto</h3>
            <p className="text-sm text-white/45">Sinais automáticos para decisão assistida.</p>
          </div>
          <div className="space-y-3 p-5">
            {(data?.insights || []).map((insight) => (
              <div key={insight} className="rounded-lg border border-white/10 bg-white/[0.04] p-3 text-sm text-white/75">
                {insight}
              </div>
            ))}
            {!(data?.insights || []).length && <p className="text-sm text-white/45">Nenhum alerta relevante no período.</p>}
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <ChartFrame title="Leads por canal" subtitle="Volume e qualificação das origens que alimentam o grupo">
          {channelChart.length ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={channelChart} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" />
                <XAxis dataKey="canal" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend />
                <Bar dataKey="leads" name="Leads" fill="#2563eb" radius={[6, 6, 0, 0]} />
                <Bar dataKey="qualificados" name="Qualificados" fill="#0f766e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart>Sem canais no período.</EmptyChart>
          )}
        </ChartFrame>

        <ChartFrame title="Funil operacional" subtitle="Mostra onde os leads ficaram parados ou encaminhados">
          {funnelChart.length ? (
            <ResponsiveContainer width="100%" height={320}>
              <FunnelChart>
                <Tooltip content={<ChartTooltip />} />
                <Funnel dataKey="value" data={funnelChart} nameKey="name" isAnimationActive>
                  {funnelChart.map((entry, index) => <Cell key={entry.name} fill={entry.fill || chartColors[index % chartColors.length]} />)}
                </Funnel>
                <Legend />
              </FunnelChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart>Sem volume suficiente para montar funil no período.</EmptyChart>
          )}
        </ChartFrame>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <ChartFrame title="Origem dos leads" subtitle="Fontes complementares ao Loft/Vista e RD">
          <SimpleBarList data={(data?.origem_leads || []).slice(0, 8)} labelKey="origem" valueKey="total" />
        </ChartFrame>

        <ChartFrame title="Mercado por finalidade" subtitle="Cuida para não misturar aluguel com venda">
          {mercadoChart.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Tooltip content={<ChartTooltip />} />
                <Pie data={mercadoChart} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={4}>
                  {mercadoChart.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart>Sem anúncios de mercado no período.</EmptyChart>
          )}
        </ChartFrame>

        <ChartFrame title="Conectores" subtitle="Saúde das integrações que alimentam o BI">
          {connectorChart.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Tooltip content={<ChartTooltip />} />
                <Pie data={connectorChart} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={4}>
                  {connectorChart.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart>Nenhum conector configurado.</EmptyChart>
          )}
        </ChartFrame>
      </div>
    </div>
  );

  const renderCampaigns = () => (
    <GlassCard>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-5">
        <div>
          <h3 className="text-lg font-semibold text-white/90">Campanhas internas e externas</h3>
          <p className="text-sm text-white/45">Meta/Google aparecerão aqui após credenciais e sincronização.</p>
        </div>
        <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm text-white/60">
          {number(campanhas.length)} campanhas
        </span>
      </div>
      <div className="divide-y divide-white/10">
        {campanhas.map((campanha) => (
          <div key={`${campanha.id}-${campanha.external_id || ''}`} className="grid grid-cols-1 gap-3 p-4 text-sm md:grid-cols-6">
            <div className="md:col-span-2">
              <p className="font-medium text-white/90">{campanha.nome}</p>
              <p className="text-xs text-white/40">{campanha.canal || 'interno'} {campanha.externa ? '· externa' : '· ERP'}</p>
            </div>
            <span className="text-white/65">{campanha.status || '-'}</span>
            <span className="text-white/65">{campanha.objetivo || campanha.tipo || '-'}</span>
            <span className="text-white/65">{campanha.grupo || 'grupo'}</span>
            <span className="text-white/65">{currency(campanha.orcamento || campanha.metricas?.investimento)}</span>
          </div>
        ))}
        {!campanhas.length && <p className="p-5 text-sm text-white/45">Nenhuma campanha cadastrada/importada.</p>}
      </div>
    </GlassCard>
  );

  const renderAttention = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard title="Sinais abertos" value={number(data?.attention?.abertos)} subtitle="Alertas de RD, Vista/Loft, portais e mídia" icon={Bot} tone="amber" />
        <KpiCard title="Leads RD no período" value={number(kpis.enviados_rd)} subtitle="Sinal para revisar RD Station" icon={Send} tone="cyan" />
        <KpiCard title="Leads Vista/Loft" value={number(kpis.enviados_vista_loft)} subtitle="Acompanhar no CRM principal" icon={CheckCircle} tone="emerald" />
      </div>

      <GlassCard>
        <div className="border-b border-white/10 p-5">
          <h3 className="text-lg font-semibold text-white/90">Caixa de atenção do Marketing</h3>
          <p className="text-sm text-white/45">Sinais para você saber onde olhar, sem transformar o ERP em outro CRM.</p>
        </div>
        <div className="divide-y divide-white/10">
          {attentionItems.map((item) => (
            <div key={item.id || item.source_key} className="grid grid-cols-1 gap-3 p-4 text-sm lg:grid-cols-7">
              <div className="lg:col-span-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${statusClass[item.severidade] || 'border-white/10 bg-white/[0.04] text-white/60'}`}>
                    {item.severidade}
                  </span>
                  <p className="font-medium text-white/90">{item.titulo}</p>
                </div>
                <p className="mt-1 text-white/55">{item.descricao}</p>
              </div>
              <span className="text-white/60">{item.ferramenta || item.source}</span>
              <span className="text-white/60">{number(item.leads_total)} leads</span>
              <span className="text-white/60">{item.status}</span>
              <a className="text-cyan-200 hover:text-cyan-100" href={`/erp${item.action_url || '/marketing'}`}>
                Abrir
              </a>
            </div>
          ))}
          {!attentionItems.length && <p className="p-5 text-sm text-white/45">Nenhum sinal de atenção agora.</p>}
        </div>
      </GlassCard>
    </div>
  );

  const renderLeads = () => (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <GlassCard>
        <div className="border-b border-white/10 p-5">
          <h3 className="text-lg font-semibold text-white/90">Origem dos leads imobiliários</h3>
        </div>
        <div className="divide-y divide-white/10">
          {(data?.origem_leads || []).map((item) => (
            <div key={item.origem} className="flex items-center justify-between p-4 text-sm">
              <span className="text-white/75">{item.origem}</span>
              <span className="font-medium text-white/90">{number(item.total)}</span>
            </div>
          ))}
          {!(data?.origem_leads || []).length && <p className="p-5 text-sm text-white/45">Sem leads imobiliários no período.</p>}
        </div>
      </GlassCard>

      <GlassCard>
        <div className="border-b border-white/10 p-5">
          <h3 className="text-lg font-semibold text-white/90">Funil complementar Imobiliária</h3>
        </div>
        <div className="divide-y divide-white/10">
          {(data?.kanban_imobiliaria || []).map((item) => (
            <div key={item.coluna} className="flex items-center justify-between p-4 text-sm">
              <span className="text-white/75">{item.coluna}</span>
              <span className="font-medium text-white/90">{number(item.total)}</span>
            </div>
          ))}
          {!(data?.kanban_imobiliaria || []).length && <p className="p-5 text-sm text-white/45">Sem funil imobiliário local.</p>}
        </div>
      </GlassCard>
    </div>
  );

  const renderConnectors = () => (
    <div className="space-y-4">
      <GlassCard className="p-5" hover={false}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white/90">Conectores de marketing</h3>
            <p className="text-sm text-white/45">RD, Meta e Google alimentam o BI com leitura agregada, sem duplicar CRM nem executar ações sem confirmação.</p>
          </div>
          <GlassButton variant="secondary" icon={RefreshCw} onClick={syncRdStation} loading={syncingRd}>
            Sincronizar RD
          </GlassButton>
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {conectores.map((connector) => {
          const Icon = connectorIcon[connector.canal] || Globe;
          return (
            <GlassCard key={connector.id} className="p-5">
              <div className="flex items-start gap-4">
                <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 text-white/75">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-white/90">{connector.nome}</h3>
                    <span className={`rounded-full border px-2 py-0.5 text-xs ${statusClass[connector.status] || 'border-white/10 bg-white/[0.04] text-white/60'}`}>
                      {connector.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-white/55">{connector.escopo}</p>
                  <p className="mt-3 text-xs text-white/35">
                    {connector.tipo} · {connector.direcao}
                    {connector.ultima_sincronizacao ? ` · sync ${new Date(connector.ultima_sincronizacao).toLocaleString('pt-BR')}` : ''}
                  </p>
                  {connector.ultimo_erro && (
                    <p className="mt-2 text-xs text-red-200/80">{connector.ultimo_erro}</p>
                  )}
                  {['meta_ads', 'google_ads'].includes(connector.canal) && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <GlassButton
                        variant="secondary"
                        icon={CheckCircle}
                        onClick={() => runAdsConnector(connector.canal, 'test')}
                        loading={busyConnector === `${connector.canal}:test`}
                      >
                        Testar
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        icon={RefreshCw}
                        onClick={() => runAdsConnector(connector.canal, 'sync')}
                        loading={busyConnector === `${connector.canal}:sync`}
                      >
                        Sincronizar
                      </GlassButton>
                    </div>
                  )}
                </div>
              </div>
            </GlassCard>
          );
        })}
        {!conectores.length && <EmptyState>Nenhum conector configurado.</EmptyState>}
      </div>
    </div>
  );

  const renderCredentials = () => {
    const groups = credentialsStatus?.groups || [];
    return (
      <div className="space-y-4">
        <GlassCard className="p-5" hover={false}>
          <div className="flex items-start gap-4">
            <div className="rounded-xl border border-cyan-300/20 bg-cyan-400/10 p-3 text-cyan-200">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white/90">Credenciais de Meta e Google Ads</h3>
              <p className="mt-1 text-sm text-white/55">
                Use esta área quando receber os dados das APIs. Tokens e secrets não aparecem de volta na tela depois de salvos.
              </p>
              {credentialsStatus?.env_path && (
                <p className="mt-2 text-xs text-white/35">Arquivo seguro no servidor: {credentialsStatus.env_path}</p>
              )}
            </div>
          </div>
        </GlassCard>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {groups.map((group) => {
            const isSaving = savingCredentials === group.canal;
            return (
              <GlassCard key={group.canal} className="p-5" hover={false}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-white/90">{group.label}</h3>
                    <p className="mt-1 text-sm text-white/55">{group.description}</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs ${group.configured ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/25 bg-amber-500/10 text-amber-100'}`}>
                    {group.configured ? 'Pronto' : `${group.missing.length} pendente(s)`}
                  </span>
                </div>

                <div className="mt-5 space-y-3">
                  {group.fields.map((field) => (
                    <label key={field.key} className="block">
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium text-white/80">
                          {field.label}
                          {field.required ? ' *' : ''}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs ${field.present ? 'bg-emerald-500/10 text-emerald-200' : 'bg-amber-500/10 text-amber-100'}`}>
                          {field.present ? `salvo ${field.masked || ''}` : 'faltando'}
                        </span>
                      </div>
                      <input
                        type={field.secret ? 'password' : 'text'}
                        value={credentialForms[group.canal]?.[field.key] || ''}
                        onChange={(event) => updateCredentialField(group.canal, field.key, event.target.value)}
                        placeholder={field.default ? `Opcional, padrão ${field.default}` : 'Cole aqui para cadastrar ou substituir'}
                        autoComplete="off"
                        className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/85 outline-none transition focus:border-cyan-300/50"
                      />
                      {field.hint && <p className="mt-1 text-xs text-white/35">{field.hint}</p>}
                    </label>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <GlassButton
                    variant="primary"
                    icon={KeyRound}
                    onClick={() => saveCredentials(group.canal)}
                    loading={isSaving}
                  >
                    Salvar credenciais
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    icon={CheckCircle}
                    onClick={() => runAdsConnector(group.canal, 'test')}
                    loading={busyConnector === `${group.canal}:test`}
                  >
                    Testar conexão
                  </GlassButton>
                </div>
              </GlassCard>
            );
          })}
          {!groups.length && <EmptyState>Credenciais ainda não carregadas.</EmptyState>}
        </div>
      </div>
    );
  };

  const renderWeeklyNewsletter = () => {
    const fieldClass = 'w-full rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm text-white outline-none transition focus:border-cyan-300/50';
    const articles = newsletterData?.eligible_articles || [];
    const themes = newsletterData?.eligible_themes || [];
    const limits = newsletterData?.editorial_limits || {
      max_themes: 6,
      featured_themes: 2,
      compact_themes: 4,
    };
    const themeLookup = new Map(themes.map((theme) => [theme.id, theme]));
    const selectedThemes = newsletterForm.theme_ids
      .map((themeId) => themeLookup.get(themeId))
      .filter(Boolean);
    const checks = newsletterPreview?.checks || {};
    const existingStatus = newsletterData?.existing_draft?.status;

    return (
      <div className="space-y-5">
        <GlassCard className="overflow-hidden" hover={false}>
          <div className="border-b border-white/10 bg-gradient-to-r from-blue-500/10 via-cyan-400/5 to-amber-400/10 p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-xl font-semibold text-white/90">MMI Weekly</h3>
                  {existingStatus && (
                    <span className={`rounded-full border px-3 py-1 text-xs ${
                      existingStatus === 'pronta_rd'
                        ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200'
                        : 'border-amber-400/25 bg-amber-500/10 text-amber-100'
                    }`}>
                      {existingStatus === 'pronta_rd' ? 'Pronta para RD Station' : 'Rascunho salvo'}
                    </span>
                  )}
                </div>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-white/50">
                  O ERP identifica os artigos aprovados que receberam link de publicação na semana,
                  monta o resumo e combina o conteúdo com até duas notícias e dois imóveis.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <GlassButton
                  variant="secondary"
                  icon={RefreshCw}
                  loading={newsletterLoading}
                  onClick={() => loadNewsletter({
                    data_inicio: newsletterForm.data_inicio,
                    data_fim: newsletterForm.data_fim,
                  })}
                >
                  Buscar publicados
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  icon={Eye}
                  loading={newsletterSaving === 'preview'}
                  onClick={generateNewsletterPreview}
                >
                  Gerar prévia
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  icon={Save}
                  loading={newsletterSaving === 'rascunho'}
                  onClick={() => saveNewsletter('rascunho')}
                >
                  Salvar rascunho
                </GlassButton>
                <GlassButton
                  variant="primary"
                  icon={CheckCircle}
                  loading={newsletterSaving === 'pronta_rd'}
                  onClick={() => saveNewsletter('pronta_rd')}
                >
                  Marcar pronta para RD
                </GlassButton>
              </div>
            </div>
          </div>

          <div className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-4">
            <label>
              <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-white/45">Início da semana</span>
              <input
                type="date"
                value={newsletterForm.data_inicio}
                onChange={(event) => {
                  setNewsletterData(null);
                  setNewsletterPreview(null);
                  setNewsletterForm((current) => ({ ...current, data_inicio: event.target.value }));
                }}
                className={fieldClass}
              />
            </label>
            <label>
              <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-white/45">Fim da semana</span>
              <input
                type="date"
                value={newsletterForm.data_fim}
                onChange={(event) => {
                  setNewsletterData(null);
                  setNewsletterPreview(null);
                  setNewsletterForm((current) => ({ ...current, data_fim: event.target.value }));
                }}
                className={fieldClass}
              />
            </label>
            <label>
              <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-white/45">Disparo sugerido</span>
              <input
                type="datetime-local"
                value={newsletterForm.send_at}
                onChange={(event) => setNewsletterForm((current) => ({ ...current, send_at: event.target.value }))}
                className={fieldClass}
              />
            </label>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <p className="text-xs uppercase tracking-wide text-white/40">Automação da semana</p>
              <p className="mt-2 text-2xl font-semibold text-white/90">{themes.length}</p>
              <p className="text-xs text-white/45">{articles.length} artigo(s) em tema(s)</p>
            </div>
            <label className="md:col-span-2 xl:col-span-4">
              <span className="mb-2 block text-xs font-medium uppercase tracking-wide text-white/45">Assunto do e-mail</span>
              <input
                value={newsletterForm.subject}
                onChange={(event) => setNewsletterForm((current) => ({ ...current, subject: event.target.value }))}
                placeholder="Gerado automaticamente a partir do principal destaque"
                className={fieldClass}
              />
            </label>
          </div>
        </GlassCard>

        <div className="grid gap-5 xl:grid-cols-[1.05fr_.95fr]">
          <div className="space-y-5">
            <GlassCard className="p-5" hover={false}>
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-white/90">Temas publicados</h4>
                  <p className="text-sm text-white/45">
                    Artigos semelhantes são reunidos em uma série. Selecione no máximo {limits.max_themes} temas.
                  </p>
                </div>
                <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-100">
                  {newsletterForm.theme_ids.length}/{limits.max_themes} tema(s)
                </span>
              </div>
              <div className="mb-4 grid gap-2 text-xs sm:grid-cols-2">
                <div className="rounded-lg border border-amber-400/15 bg-amber-500/[0.06] px-3 py-2 text-amber-100/80">
                  Os 2 primeiros selecionados recebem resumo e imagem.
                </div>
                <div className="rounded-lg border border-cyan-400/15 bg-cyan-500/[0.06] px-3 py-2 text-cyan-100/80">
                  Os 4 seguintes aparecem como chamadas compactas.
                </div>
              </div>
              <div className="space-y-3">
                {themes.map((theme) => {
                  const selectedIndex = selectedThemes.findIndex((item) => item.id === theme.id);
                  const isSelected = selectedIndex >= 0;
                  const isFeatured = isSelected && selectedIndex < limits.featured_themes;
                  return (
                  <label
                    key={theme.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                      isSelected
                        ? 'border-cyan-300/25 bg-cyan-400/[0.07]'
                        : 'border-white/10 bg-white/[0.025] hover:bg-white/[0.04]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleNewsletterTheme(theme.id)}
                      className="mt-1 h-4 w-4 rounded border-white/20 bg-slate-900"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {isFeatured && (
                          <span className="rounded-full border border-amber-400/25 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-100">
                            Resumo completo
                          </span>
                        )}
                        {isSelected && !isFeatured && (
                          <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2 py-0.5 text-[11px] text-cyan-100">
                            Chamada compacta
                          </span>
                        )}
                        {theme.is_series && (
                          <span className="rounded-full border border-violet-400/20 bg-violet-500/10 px-2 py-0.5 text-[11px] text-violet-100">
                            Série · {theme.article_count} artigos
                          </span>
                        )}
                        {theme.category && <span className="text-xs text-white/35">{theme.category}</span>}
                        <span className="text-xs text-white/35">
                          {new Date(`${theme.published_on}T12:00:00`).toLocaleDateString('pt-BR')}
                        </span>
                        {isSelected && (
                          <span className="ml-auto flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                moveNewsletterTheme(theme.id, -1);
                              }}
                              disabled={selectedIndex === 0}
                              aria-label={`Subir ${theme.title}`}
                              title="Subir na newsletter"
                              className="rounded border border-white/10 px-2 py-0.5 text-xs text-white/55 hover:bg-white/10 disabled:opacity-25"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                moveNewsletterTheme(theme.id, 1);
                              }}
                              disabled={selectedIndex === selectedThemes.length - 1}
                              aria-label={`Descer ${theme.title}`}
                              title="Descer na newsletter"
                              className="rounded border border-white/10 px-2 py-0.5 text-xs text-white/55 hover:bg-white/10 disabled:opacity-25"
                            >
                              ↓
                            </button>
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-medium leading-6 text-white/85">{theme.title}</p>
                      <p className="mt-1 line-clamp-2 text-sm leading-5 text-white/45">{theme.summary}</p>
                      {theme.is_series && (
                        <div className="mt-2 space-y-1 border-l border-violet-300/20 pl-3">
                          {(theme.articles || []).slice(0, 5).map((article) => (
                            <p key={article.id} className="truncate text-xs text-white/40">• {article.title}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                  );
                })}
                {!themes.length && (
                  <div className="rounded-xl border border-dashed border-amber-400/25 bg-amber-500/[0.06] p-5 text-sm leading-6 text-amber-100">
                    Nenhum conteúdo atende aos três critérios. Aprove o artigo, salve o link publicado
                    e confirme uma data de publicação dentro desta semana.
                  </div>
                )}
              </div>
            </GlassCard>

            <GlassCard className="p-5" hover={false}>
              <div className="mb-4">
                <h4 className="font-semibold text-white/90">Radar da semana</h4>
                <p className="text-sm text-white/45">
                  O sistema pesquisa e seleciona até duas notícias publicadas no mesmo período dos artigos.
                  A busca é atualizada sempre que você gera a prévia.
                </p>
              </div>
              <div className="space-y-3">
                {newsletterForm.news_items.filter((item) => item.title?.trim()).map((item, index) => (
                  <a
                    key={`${item.url || item.title}-${index}`}
                    href={item.url || undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-xl border border-amber-300/15 bg-amber-500/[0.05] p-4 transition hover:bg-amber-500/[0.08]"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/75">
                        Notícia {index + 1} · {item.source || 'Fonte identificada na busca'}
                        {item.published_on
                          ? ` · ${new Date(`${item.published_on}T12:00:00`).toLocaleDateString('pt-BR')}`
                          : ''}
                      </p>
                      <span className="text-xs text-white/30">selecionada automaticamente</span>
                    </div>
                    <p className="mt-2 font-medium leading-6 text-white/85">{item.title}</p>
                    <p className="mt-1 text-sm leading-5 text-white/45">{item.summary}</p>
                  </a>
                ))}
                {!newsletterForm.news_items.some((item) => item.title?.trim()) && (
                  <div className="rounded-xl border border-dashed border-amber-300/20 bg-amber-500/[0.04] p-4 text-sm leading-6 text-amber-100/70">
                    As notícias serão pesquisadas no período de {newsletterForm.data_inicio.split('-').reverse().join('/')}
                    {' '}a {newsletterForm.data_fim.split('-').reverse().join('/')} ao clicar em “Gerar prévia”.
                  </div>
                )}
              </div>
            </GlassCard>

            <GlassCard className="p-5" hover={false}>
              <div className="mb-4">
                <h4 className="font-semibold text-white/90">Painel do investidor</h4>
                <p className="text-sm text-white/45">
                  Indicadores oficiais escolhidos conforme os assuntos desta edição e atualizados na geração da prévia.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {newsletterForm.investor_metrics.filter((item) => item.value?.trim()).map((item, index) => (
                  <a
                    key={`${item.label}-${index}`}
                    href={item.url || undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl border border-cyan-300/15 bg-cyan-500/[0.05] p-4 transition hover:bg-cyan-500/[0.08]"
                  >
                    <p className="text-2xl font-semibold text-amber-200">{item.value}</p>
                    <p className="mt-1 text-sm leading-5 text-white/75">{item.label}</p>
                    <p className="mt-2 text-xs text-white/35">{item.source || 'Fonte oficial'}</p>
                  </a>
                ))}
                {!newsletterForm.investor_metrics.some((item) => item.value?.trim()) && (
                  <div className="rounded-xl border border-dashed border-cyan-300/20 bg-cyan-500/[0.04] p-4 text-sm leading-6 text-cyan-100/70 md:col-span-2">
                    O painel será calculado automaticamente ao gerar a prévia: financiamento usa Selic,
                    construção e lançamentos usam INCC-M, e os demais temas usam o preço médio por m².
                  </div>
                )}
              </div>
              {(newsletterForm.research?.warnings || []).map((warning) => (
                <p key={warning} className="mt-3 rounded-lg border border-amber-400/15 bg-amber-500/[0.05] px-3 py-2 text-xs text-amber-100/75">
                  {warning}
                </p>
              ))}
            </GlassCard>
          </div>

          <div className="space-y-5">
            <GlassCard className="p-5" hover={false}>
              <div className="mb-4">
                <h4 className="font-semibold text-white/90">Oportunidades da semana</h4>
                <p className="text-sm text-white/45">Dois imóveis com informação objetiva e chamada direta.</p>
              </div>
              <div className="space-y-4">
                {newsletterForm.properties.map((item, index) => (
                  <div key={`property-${index}`} className="rounded-xl border border-white/10 bg-gradient-to-br from-blue-500/[0.08] to-white/[0.02] p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-cyan-100/80">Imóvel {index + 1}</p>
                      <span className={`h-2.5 w-2.5 rounded-full ${item.title.trim() ? 'bg-emerald-400' : 'bg-white/20'}`} />
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <input value={item.code} onChange={(event) => updateNewsletterListItem('properties', index, 'code', event.target.value)} placeholder="Código do imóvel" className={fieldClass} />
                      <input value={item.price} onChange={(event) => updateNewsletterListItem('properties', index, 'price', event.target.value)} placeholder="Preço ou condição" className={fieldClass} />
                      <input value={item.title} onChange={(event) => updateNewsletterListItem('properties', index, 'title', event.target.value)} placeholder="Título comercial do imóvel" className={`${fieldClass} md:col-span-2`} />
                      <input value={item.details} onChange={(event) => updateNewsletterListItem('properties', index, 'details', event.target.value)} placeholder="Suítes • vagas • área • localização" className={`${fieldClass} md:col-span-2`} />
                      <input value={item.badge} onChange={(event) => updateNewsletterListItem('properties', index, 'badge', event.target.value)} placeholder="Destaque: lançamento, promoção, melhor valor…" className={`${fieldClass} md:col-span-2`} />
                      <input value={item.image_url} onChange={(event) => updateNewsletterListItem('properties', index, 'image_url', event.target.value)} placeholder="URL pública da imagem" className={`${fieldClass} md:col-span-2`} />
                      <div className="flex gap-2">
                        <input value={item.url} onChange={(event) => updateNewsletterListItem('properties', index, 'url', event.target.value)} placeholder="Link de detalhes — use para preencher os dados automaticamente" className={fieldClass} />
                        <GlassButton
                          type="button"
                          variant="secondary"
                          icon={RefreshCw}
                          loading={newsletterPropertyExtracting === index}
                          onClick={() => completeWeeklyPropertyFromLink(index)}
                        >
                          Completar pelo link
                        </GlassButton>
                      </div>
                      <input value={item.whatsapp_url} onChange={(event) => updateNewsletterListItem('properties', index, 'whatsapp_url', event.target.value)} placeholder="Link do WhatsApp" className={fieldClass} />
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>

            <GlassCard className="p-5" hover={false}>
              <h4 className="font-semibold text-white/90">Checklist do disparo</h4>
              <div className="mt-4 space-y-2 text-sm">
                {[
                  [Number(checks.themes || newsletterForm.theme_ids.length) > 0, 'Temas aprovados e publicados incluídos'],
                  [newsletterForm.theme_ids.length <= limits.max_themes, `Limite de ${limits.max_themes} temas respeitado`],
                  [Number(checks.news_items || newsletterForm.news_items.filter((item) => item.title?.trim()).length) === 2, 'Duas notícias pesquisadas no período'],
                  [Number(checks.investor_metrics || newsletterForm.investor_metrics.filter((item) => item.value?.trim()).length) > 0, 'Painel do investidor atualizado com fonte'],
                  [Number(checks.properties || newsletterForm.properties.filter((item) => item.title.trim()).length) === 2, 'Dois imóveis preenchidos'],
                  [Boolean(newsletterForm.subject.trim()), 'Assunto definido'],
                  [Boolean(newsletterPreview?.html), 'Prévia HTML gerada'],
                ].map(([done, label]) => (
                  <div key={label} className="flex items-center gap-2">
                    <CheckCircle className={`h-4 w-4 ${done ? 'text-emerald-300' : 'text-white/20'}`} />
                    <span className={done ? 'text-white/75' : 'text-white/35'}>{label}</span>
                  </div>
                ))}
              </div>
              {newsletterPreview?.html && (
                <GlassButton
                  variant="secondary"
                  icon={Copy}
                  onClick={() => copyPublicationField('newsletter_html', newsletterPreview.html)}
                  className="mt-5 w-full"
                >
                  {copiedField === 'newsletter_html' ? 'HTML copiado' : 'Copiar HTML para o RD Station'}
                </GlassButton>
              )}
            </GlassCard>
          </div>
        </div>

        {newsletterPreview?.html && (
          <GlassCard className="overflow-hidden" hover={false}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4">
              <div>
                <h4 className="font-semibold text-white/90">Prévia da edição</h4>
                <p className="text-xs text-white/45">{newsletterPreview.subject || newsletterForm.subject}</p>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs ${
                checks.ready_for_rd_station
                  ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200'
                  : 'border-amber-400/25 bg-amber-500/10 text-amber-100'
              }`}>
                {checks.ready_for_rd_station ? 'Estrutura completa' : 'Complete o checklist'}
              </span>
            </div>
            <div className="h-[780px] bg-slate-200 p-3">
              <iframe title="Prévia da newsletter" srcDoc={newsletterPreview.html} className="h-full w-full rounded-xl border-0 bg-white" />
            </div>
          </GlassCard>
        )}
      </div>
    );
  };

  const renderOpportunityNewsletter = () => {
    const fieldClass = 'w-full rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm text-white outline-none transition focus:border-cyan-300/50';
    const checks = opportunityPreview?.checks || {};
    const campaigns = opportunityData?.campaigns || [];
    const loadCampaign = (campaign) => {
      const metadata = campaign.metadata || {};
      setOpportunityForm({ ...createOpportunityForm(), ...metadata, campaign_id: campaign.id, source_url: metadata.property?.source_url || metadata.property?.url || '', property: metadata.property || createOpportunityForm().property });
      setOpportunityPreview({ html: campaign.html, subject: campaign.subject, metadata, checks: metadata.checks || {} });
    };
    return (
      <div className="space-y-5">
        <GlassCard className="overflow-hidden" hover={false}>
          <div className="bg-gradient-to-r from-amber-500/10 via-cyan-400/5 to-blue-500/10 p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-white/90">Oportunidade especial</h3>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-white/50">Cole o link, descreva a condição real e revise uma campanha focada em um único imóvel. A extração sugere dados públicos; preço, imagem, disponibilidade e prazo sempre ficam sob sua confirmação.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <GlassButton variant="secondary" icon={RefreshCw} loading={opportunityLoading} onClick={loadOpportunityCampaigns}>Atualizar lista</GlassButton>
                <GlassButton variant="secondary" icon={Eye} loading={opportunitySaving === 'preview'} onClick={generateOpportunityPreview}>Gerar prévia</GlassButton>
                <GlassButton variant="secondary" icon={Save} loading={opportunitySaving === 'rascunho'} onClick={() => saveOpportunity('rascunho')}>Salvar rascunho</GlassButton>
                <GlassButton variant="primary" icon={CheckCircle} loading={opportunitySaving === 'pronta_rd'} onClick={() => saveOpportunity('pronta_rd')}>Marcar pronta para RD</GlassButton>
              </div>
            </div>
          </div>
        </GlassCard>

        <div className="grid gap-5 xl:grid-cols-[1.1fr_.9fr]">
          <div className="space-y-5">
            <GlassCard className="p-5" hover={false}>
              <h4 className="font-semibold text-white/90">1. Fonte e condição comercial</h4>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="flex gap-2 md:col-span-2"><input value={opportunityForm.source_url} onChange={(event) => updateOpportunity('source_url', event.target.value)} placeholder="Link público do imóvel ou empreendimento" className={fieldClass} /><GlassButton variant="secondary" icon={Link2} loading={opportunitySaving === 'extract'} onClick={extractOpportunity}>Ler link</GlassButton></div>
                <label><span className="mb-2 block text-xs text-white/45">Tipo</span><select value={opportunityForm.opportunity_type} onChange={(event) => updateOpportunity('opportunity_type', event.target.value)} className={fieldClass}><option value="pronto">Imóvel pronto</option><option value="lancamento">Lançamento</option></select></label>
                <label><span className="mb-2 block text-xs text-white/45">Validade real (opcional)</span><input type="date" value={opportunityForm.valid_until} onChange={(event) => updateOpportunity('valid_until', event.target.value)} className={fieldClass} /></label>
                <label className="md:col-span-2"><span className="mb-2 block text-xs text-white/45">Vantagem ou condição comercial</span><textarea value={opportunityForm.advantage} onChange={(event) => updateOpportunity('advantage', event.target.value)} placeholder="Ex.: Unidade decorada, entrada facilitada e condição válida até sexta." rows="3" className={fieldClass} /></label>
                <input value={opportunityForm.whatsapp_url} onChange={(event) => updateOpportunity('whatsapp_url', event.target.value)} placeholder="Link de WhatsApp (opcional)" className={`${fieldClass} md:col-span-2`} />
              </div>
            </GlassCard>

            <GlassCard className="p-5" hover={false}>
              <h4 className="font-semibold text-white/90">2. Dados extraídos e revisão</h4>
              <p className="mt-1 text-sm text-white/45">Ajuste qualquer dado antes de gerar o e-mail. Nenhum campo é tratado como confirmação automática.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <input value={opportunityForm.property.title} onChange={(event) => updateOpportunityProperty('title', event.target.value)} placeholder="Nome do imóvel" className={`${fieldClass} md:col-span-2`} />
                <input value={opportunityForm.property.price} onChange={(event) => updateOpportunityProperty('price', event.target.value)} placeholder="Preço ou condição exibida" className={fieldClass} />
                <input value={opportunityForm.property.details} onChange={(event) => updateOpportunityProperty('details', event.target.value)} placeholder="Suítes • vagas • área • localização" className={fieldClass} />
                <input value={opportunityForm.property.image_url} onChange={(event) => updateOpportunityProperty('image_url', event.target.value)} placeholder="URL pública da imagem" className={`${fieldClass} md:col-span-2`} />
                <input value={opportunityForm.property.url} onChange={(event) => updateOpportunityProperty('url', event.target.value)} placeholder="Link de destino do botão" className={`${fieldClass} md:col-span-2`} />
                <textarea value={opportunityForm.property.description} onChange={(event) => updateOpportunityProperty('description', event.target.value)} placeholder="Descrição extraída (referência para revisão)" rows="3" className={`${fieldClass} md:col-span-2`} />
              </div>
            </GlassCard>

            <GlassCard className="p-5" hover={false}>
              <h4 className="font-semibold text-white/90">3. Texto do e-mail</h4><p className="mt-1 text-sm text-white/45">A geração assistida cria uma versão inicial baseada no link e na vantagem. Você pode editar tudo.</p>
              <div className="mt-4 grid gap-3"><input value={opportunityForm.subject} onChange={(event) => updateOpportunity('subject', event.target.value)} placeholder="Assunto do e-mail" className={fieldClass} /><input value={opportunityForm.preheader} onChange={(event) => updateOpportunity('preheader', event.target.value)} placeholder="Preheader" className={fieldClass} /><input value={opportunityForm.heading} onChange={(event) => updateOpportunity('heading', event.target.value)} placeholder="Título principal" className={fieldClass} /><textarea value={opportunityForm.body} onChange={(event) => updateOpportunity('body', event.target.value)} placeholder="Texto principal" rows="4" className={fieldClass} /></div>
            </GlassCard>
          </div>

          <div className="space-y-5">
            <GlassCard className="p-5" hover={false}><h4 className="font-semibold text-white/90">Checklist do disparo</h4><div className="mt-4 space-y-2 text-sm">{[[checks.property_link || Boolean(opportunityForm.property.url), 'Link de destino definido'], [checks.title || Boolean(opportunityForm.property.title.trim()), 'Nome do imóvel revisado'], [checks.image || Boolean(opportunityForm.property.image_url.trim()), 'Imagem pública confirmada'], [checks.advantage || Boolean(opportunityForm.advantage.trim()), 'Vantagem comercial informada'], [Boolean(opportunityPreview?.html), 'Prévia HTML gerada']].map(([done, label]) => <div key={label} className="flex items-center gap-2"><CheckCircle className={`h-4 w-4 ${done ? 'text-emerald-300' : 'text-white/20'}`} /><span className={done ? 'text-white/75' : 'text-white/35'}>{label}</span></div>)}</div>{opportunityPreview?.html && <GlassButton variant="secondary" icon={Copy} onClick={() => copyPublicationField('opportunity_html', opportunityPreview.html)} className="mt-5 w-full">{copiedField === 'opportunity_html' ? 'HTML copiado' : 'Copiar HTML para o RD Station'}</GlassButton>}</GlassCard>
            <GlassCard className="p-5" hover={false}><h4 className="font-semibold text-white/90">Campanhas recentes</h4><div className="mt-3 space-y-2">{campaigns.map((campaign) => <button key={campaign.id} onClick={() => loadCampaign(campaign)} className="w-full rounded-lg border border-white/10 bg-white/[0.03] p-3 text-left hover:bg-white/[0.06]"><p className="truncate text-sm font-medium text-white/80">{campaign.name}</p><p className="mt-1 text-xs text-white/40">{campaign.status === 'pronta_rd' ? 'Pronta para RD' : 'Rascunho'} · {campaign.updated_at ? new Date(campaign.updated_at).toLocaleDateString('pt-BR') : ''}</p></button>)}{!campaigns.length && <p className="text-sm text-white/40">Nenhuma oportunidade salva ainda.</p>}</div></GlassCard>
          </div>
        </div>
        {opportunityPreview?.html && <GlassCard className="overflow-hidden" hover={false}><div className="border-b border-white/10 p-4"><h4 className="font-semibold text-white/90">Prévia da oportunidade</h4><p className="text-xs text-white/45">{opportunityPreview.subject || opportunityForm.subject}</p></div><div className="h-[780px] bg-slate-200 p-3"><iframe title="Prévia da oportunidade especial" srcDoc={opportunityPreview.html} className="h-full w-full rounded-xl border-0 bg-white" /></div></GlassCard>}
      </div>
    );
  };

  const renderNewsletter = () => (
    <div className="space-y-5">
      <GlassCard className="p-2" hover={false}><div className="flex flex-wrap items-center justify-between gap-2"><div className="flex flex-wrap gap-2"><button onClick={() => setNewsletterMode('weekly')} className={`rounded-lg px-4 py-2 text-sm ${newsletterMode === 'weekly' ? 'bg-white/10 text-white' : 'text-white/55 hover:bg-white/[0.04]'}`}>Newsletter semanal</button><button onClick={() => setNewsletterMode('opportunity')} className={`rounded-lg px-4 py-2 text-sm ${newsletterMode === 'opportunity' ? 'bg-white/10 text-white' : 'text-white/55 hover:bg-white/[0.04]'}`}>Oportunidade especial</button></div><a href={`${process.env.PUBLIC_URL || ''}/manuals/guia-newsletter-marketing.md`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-cyan-100 hover:bg-white/[0.04]"><FileText className="h-4 w-4" />Como usar</a></div></GlassCard>
      {newsletterMode === 'weekly' ? renderWeeklyNewsletter() : renderOpportunityNewsletter()}
    </div>
  );

  const renderBlog = () => (
    <>
      <GlassCard>
        <div className="border-b border-white/10 p-5">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
            <div>
              <h3 className="text-lg font-semibold text-white/90">Central editorial</h3>
              <p className="text-sm text-white/45">Localize, priorize e publique artigos sem perder o contexto entre pautas relacionadas.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {['pending', 'approved', 'discarded', 'review', 'needs_review'].map((status) => (
                <button key={status} onClick={() => updateContentFilter('status', contentFilters.status === status ? 'all' : status)} className={`rounded-full border px-3 py-1.5 transition ${contentFilters.status === status ? 'border-cyan-300/45 bg-cyan-400/15 text-cyan-100' : 'border-white/10 bg-white/[0.03] text-white/55 hover:bg-white/[0.08]'}`}>
                  {labelPostStatus(status)} · {posts.filter((post) => post.status === status).length}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <input value={contentFilters.query} onChange={(event) => updateContentFilter('query', event.target.value)} placeholder="Buscar por ID, título ou palavra-chave" className="xl:col-span-2 w-full rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-300/50" />
            <select value={contentFilters.status} onChange={(event) => updateContentFilter('status', event.target.value)} className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-300/50"><option value="all">Todos os status</option>{['pending', 'approved', 'review', 'needs_review', 'discarded'].map((status) => <option key={status} value={status}>{labelPostStatus(status)}</option>)}</select>
            <select value={contentFilters.city} onChange={(event) => updateContentFilter('city', event.target.value)} className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-300/50"><option value="all">Todas as cidades</option>{contentFacets.cities.map((city) => <option key={city} value={city}>{city}</option>)}</select>
            <select value={contentFilters.category} onChange={(event) => updateContentFilter('category', event.target.value)} className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-300/50"><option value="all">Todos os tópicos</option>{contentFacets.categories.map((category) => <option key={category} value={category}>{category}</option>)}</select>
            <select value={contentFilters.sort} onChange={(event) => updateContentFilter('sort', event.target.value)} className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-300/50"><option value="id_asc">ID: menor → maior</option><option value="id_desc">ID: maior → menor</option><option value="oldest">Mais antigo</option><option value="newest">Mais recente</option></select>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <select value={contentFilters.collection} onChange={(event) => updateContentFilter('collection', event.target.value)} className="rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-xs text-white outline-none focus:border-cyan-300/50"><option value="all">Todas as coleções</option><option value="without_collection">Sem coleção identificada</option>{contentFacets.collections.map((collection) => <option key={collection} value={collection}>Coleção {collection}</option>)}</select>
            <div className="flex items-center gap-3 text-xs text-white/45"><span><strong className="text-white/80">{filteredPosts.length}</strong> de {posts.length} artigos</span><button onClick={() => setContentFilters({ query: '', status: 'all', city: 'all', category: 'all', collection: 'all', sort: 'id_asc' })} className="text-cyan-200 hover:text-cyan-100">Limpar filtros</button></div>
          </div>
        </div>
        <div className="divide-y divide-white/10">
          {filteredPosts.map((post) => (
            <div key={post.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><span className="rounded-md border border-cyan-300/25 bg-cyan-400/10 px-2 py-0.5 font-mono text-xs font-semibold text-cyan-100">ID {post.id}</span><p className="font-medium text-white/90">{post.titulo || post.topic}</p></div>
                  <p className="text-xs text-white/40">
                    {post.created_at ? new Date(post.created_at).toLocaleString('pt-BR') : '-'}
                    {post.categoria ? ` · ${post.categoria}` : ''}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">{citiesForPost(post).map((city) => <span key={city} className="rounded-full border border-blue-300/20 bg-blue-400/[0.08] px-2 py-0.5 text-[11px] text-blue-100">{city}</span>)}{post.hub_work_id && <span className="rounded-full border border-violet-300/20 bg-violet-400/[0.08] px-2 py-0.5 text-[11px] text-violet-100">Coleção vinculada</span>}</div>
                </div>
                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/60">{labelPostStatus(post.status)}</span>
                {(() => { const pkg = socialPackageFor(post.id); const label = pkg ? (pkg.state === 'approved' ? 'Social aprovado' : pkg.payload?.ab_comparison?.status === 'failed' ? 'Social com falha' : 'Rascunho social') : 'Social pendente'; const tone = pkg?.state === 'approved' ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200' : pkg?.payload?.ab_comparison?.status === 'failed' ? 'border-red-400/25 bg-red-500/10 text-red-100' : 'border-amber-400/25 bg-amber-500/10 text-amber-100'; return <span className={`rounded-full border px-3 py-1 text-xs ${tone}`}>{label}</span>; })()}
                <div className="flex flex-wrap gap-2">
                  {socialPackageFor(post.id) ? <GlassButton variant="secondary" onClick={() => openSocialPackage(socialPackageFor(post.id).id)}>Abrir pacote social</GlassButton> : post.status !== 'discarded' && <GlassButton variant="secondary" loading={creatingSocialForArticleId === post.id} onClick={() => generateSocialPackageFromArticle(post)}>Preparar pacote completo</GlassButton>}
                  <GlassButton variant="secondary" icon={Eye} onClick={() => openPost(post)}>
                    Abrir
                  </GlassButton>
                </div>
              </div>
            </div>
          ))}
          {!filteredPosts.length && <p className="p-5 text-sm text-white/45">Nenhum artigo corresponde aos filtros selecionados.</p>}
        </div>
      </GlassCard>

      {selectedPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-2 sm:p-4">
          <div className="flex h-[calc(100dvh-1rem)] w-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-white/15 bg-slate-950 shadow-2xl sm:h-[92vh]">
            <div className="shrink-0 border-b border-white/10 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                <h3 className="font-semibold text-white/90">{selectedPost.titulo || selectedPost.topic}</h3>
                <p className="text-xs text-white/40">Status: {selectedPost.status} · ID {selectedPost.id}{postHasUnsavedChanges ? ' · alterações não salvas' : ''}</p>
              </div>
                <button onClick={closePost} aria-label="Fechar artigo" className="shrink-0 rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <div className="mt-3 flex max-w-full gap-2 overflow-x-auto pb-1">
                <GlassButton variant={postView === 'preview' ? 'primary' : 'secondary'} onClick={() => setPostView('preview')}>
                  Visualizar
                </GlassButton>
                <GlassButton variant={postView === 'html' ? 'primary' : 'secondary'} onClick={() => setPostView('html')}>
                  Editar HTML
                </GlassButton>
                <GlassButton variant={postView === 'metadata' ? 'primary' : 'secondary'} onClick={() => setPostView('metadata')}>
                  Publicação
                </GlassButton>
                <GlassButton variant={postView === 'social' ? 'primary' : 'secondary'} onClick={() => setPostView('social')}>
                  Redes sociais
                </GlassButton>
                <GlassButton variant={postView === 'revision' ? 'primary' : 'secondary'} onClick={() => setPostView('revision')}>
                  Solicitar alteração
                </GlassButton>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
              {postRelationships && (
                <div className="max-h-52 shrink-0 overflow-y-auto border-b border-white/10 bg-slate-950 p-4">
                  <div className="mx-auto grid max-w-7xl gap-3 lg:grid-cols-2">
                    <div className="rounded-xl border border-violet-300/20 bg-violet-400/[0.06] p-3">
                      <div className="flex items-center justify-between gap-3"><div><p className="text-sm font-semibold text-violet-100">Coleção vinculada</p><p className="text-xs text-white/45">{postRelationships.collection?.id ? 'Ligação confirmada pelo Hub Editorial.' : 'Este artigo ainda não tem uma coleção confirmada.'}</p></div>{postRelationships.collection?.items?.length > 0 && <span className="rounded-full bg-violet-400/10 px-2 py-1 text-xs text-violet-100">{postRelationships.collection.items.length} artigo(s)</span>}</div>
                      {postRelationships.collection?.items?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{postRelationships.collection.items.map((item) => <button key={item.id} onClick={() => openPost(item)} className="rounded-lg border border-violet-300/20 bg-slate-950/60 px-2 py-1 text-left text-xs text-violet-100 hover:bg-violet-400/10">ID {item.id} · {item.titulo || item.topic}</button>)}</div>}
                    </div>
                    <div className="rounded-xl border border-cyan-300/20 bg-cyan-400/[0.05] p-3">
                      <p className="text-sm font-semibold text-cyan-100">Artigos relacionados</p><p className="text-xs text-white/45">Sugestões por cidade ou tópico; não indicam uma coleção confirmada.</p>
                      {postRelationships.suggestions?.length ? <div className="mt-3 flex flex-wrap gap-2">{postRelationships.suggestions.map(({ post: item, reasons }) => <button key={item.id} onClick={() => openPost(item)} title={reasons.join(' · ')} className="rounded-lg border border-cyan-300/20 bg-slate-950/60 px-2 py-1 text-left text-xs text-cyan-100 hover:bg-cyan-400/10">ID {item.id} · {item.titulo || item.topic}</button>)}</div> : <p className="mt-3 text-xs text-white/35">Nenhuma sugestão encontrada.</p>}
                    </div>
                  </div>
                </div>
              )}
              <div className="min-h-0 flex-1">
              {postView === 'preview' ? (
                <iframe title="Prévia do artigo" srcDoc={postHtml} className="h-full w-full border-0" />
              ) : postView === 'html' ? (
                <textarea
                  value={postHtml}
                  onChange={(event) => setPostHtml(event.target.value)}
                  spellCheck={false}
                  className="h-full w-full resize-none bg-slate-950 p-5 font-mono text-sm text-slate-100 outline-none"
                />
              ) : postView === 'metadata' ? (
                <div className="h-full overflow-y-auto bg-slate-950 p-6">
                  <div className="mx-auto grid max-w-4xl gap-5 md:grid-cols-2">
                    {[
                      ['titulo', 'Título'],
                      ['slug', 'Slug'],
                      ['blog_meta', 'Meta descrição'],
                      ['blog_keywords', '5 tags, separadas por vírgula'],
                      ['resumo', 'Resumo para publicação'],
                      ['publication_date', 'Data e hora de publicação'],
                      ['instagram_caption', 'Legenda do Instagram'],
                      ['instagram_hashtags', 'Hashtags do Instagram'],
                      ['tiktok_script', 'Roteiro do TikTok'],
                      ['facebook_post', 'Post do Facebook'],
                    ].map(([key, label]) => (
                      <label key={key} className={key === 'resumo' || key === 'blog_meta' || key === 'instagram_caption' || key === 'tiktok_script' || key === 'facebook_post' ? 'md:col-span-2' : ''}>
                        <span className="mb-2 flex items-center justify-between gap-3 text-sm font-medium text-white/75">
                          <span>{label}</span>
                          <button
                            type="button"
                            onClick={() => copyPublicationField(key, postMetadata[key])}
                            disabled={!postMetadata[key]}
                            title={`Copiar ${label.toLowerCase()}`}
                            aria-label={`Copiar ${label.toLowerCase()}`}
                            className="rounded-lg border border-white/10 bg-white/[0.04] p-2 text-white/55 transition hover:border-cyan-300/35 hover:bg-cyan-400/10 hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            {copiedField === key ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </span>
                        {key === 'resumo' || key === 'blog_meta' || key === 'instagram_caption' || key === 'tiktok_script' || key === 'facebook_post' ? (
                          <textarea
                            value={postMetadata[key] || ''}
                            onChange={(event) => setPostMetadata((current) => ({ ...current, [key]: event.target.value }))}
                            className="min-h-28 w-full rounded-xl border border-white/10 bg-white/[0.05] p-3 text-sm text-white outline-none focus:border-cyan-300/50"
                          />
                        ) : (
                          <input
                            type={key === 'publication_date' ? 'datetime-local' : 'text'}
                            value={postMetadata[key] || ''}
                            onChange={(event) => setPostMetadata((current) => ({ ...current, [key]: event.target.value }))}
                            step={key === 'publication_date' ? 60 : undefined}
                            title={key === 'publication_date' ? 'Escolha a data e o horário de publicação' : undefined}
                            className="w-full rounded-xl border border-white/10 bg-white/[0.05] p-3 text-sm text-white outline-none focus:border-cyan-300/50"
                          />
                        )}
                      </label>
                    ))}
                    <label className="md:col-span-2">
                      <span className="mb-2 flex items-center justify-between gap-3 text-sm font-medium text-white/75">
                        <span>Categoria</span>
                        <button
                          type="button"
                          onClick={() => copyPublicationField('categoria', postMetadata.categoria)}
                          disabled={!postMetadata.categoria}
                          title="Copiar categoria"
                          aria-label="Copiar categoria"
                          className="rounded-lg border border-white/10 bg-white/[0.04] p-2 text-white/55 transition hover:border-cyan-300/35 hover:bg-cyan-400/10 hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          {copiedField === 'categoria' ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </span>
                      <select
                        value={postMetadata.categoria || ''}
                        onChange={(event) => setPostMetadata((current) => ({ ...current, categoria: event.target.value }))}
                        className="w-full rounded-xl border border-white/10 bg-slate-900 p-3 text-sm text-white outline-none focus:border-cyan-300/50"
                      >
                        <option value="">Selecione uma categoria</option>
                        {articleCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                      </select>
                    </label>
                    <div className="md:col-span-2 rounded-2xl border border-cyan-300/15 bg-cyan-400/[0.05] p-5">
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl border border-cyan-300/20 bg-cyan-400/10 p-2 text-cyan-200">
                          <Link2 className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-white/85">Link da versão final publicada</span>
                          <p className="mt-1 text-xs text-white/45">
                            Cole o link depois de revisar e publicar. O Nexus salva a URL e confere o conteúdo online com a versão aprovada.
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                        <input
                          type="url"
                          value={postMetadata.published_url || ''}
                          onChange={(event) => {
                            setPostMetadata((current) => ({ ...current, published_url: event.target.value }));
                            setPublishedValidation(null);
                          }}
                          placeholder="https://manicamarin.com.br/artigo/..."
                          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-slate-950/60 p-3 text-sm text-white outline-none focus:border-cyan-300/50"
                        />
                        <GlassButton
                          variant="primary"
                          icon={RefreshCw}
                          loading={validatingPublished}
                          onClick={validatePublishedArticle}
                        >
                          Salvar link e conferir
                        </GlassButton>
                      </div>
                      {publishedValidation && (
                        <div className={`mt-4 rounded-xl border p-4 ${
                          publishedValidation.status === 'identical'
                            ? 'border-emerald-400/25 bg-emerald-500/10'
                            : publishedValidation.status === 'minor_changes'
                              ? 'border-amber-400/25 bg-amber-500/10'
                              : 'border-red-400/25 bg-red-500/10'
                        }`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-medium text-white/85">
                              {publishedValidation.message || (
                                publishedValidation.status === 'identical'
                                  ? 'Versão publicada conferida e igual ao ERP.'
                                  : publishedValidation.status === 'minor_changes'
                                    ? 'Pequenos ajustes encontrados na versão publicada.'
                                    : 'Alterações relevantes encontradas na versão publicada.'
                              )}
                            </p>
                            {publishedValidation.similarity_percent !== null && publishedValidation.similarity_percent !== undefined && (
                              <span className="rounded-full border border-white/10 bg-slate-950/40 px-3 py-1 text-xs text-white/65">
                                {publishedValidation.similarity_percent}% de correspondência
                              </span>
                            )}
                          </div>
                          {publishedValidation.differences?.length > 0 && (
                            <details className="mt-3 text-xs text-white/60">
                              <summary className="cursor-pointer font-medium text-white/75">Ver diferenças encontradas</summary>
                              <div className="mt-3 space-y-3">
                                {publishedValidation.differences.map((difference, index) => (
                                  <div key={`${difference.type}-${index}`} className="rounded-lg border border-white/10 bg-slate-950/45 p-3">
                                    {difference.erp && <p><span className="text-white/35">No ERP:</span> {difference.erp}</p>}
                                    {difference.published && <p className="mt-1"><span className="text-white/35">Publicado:</span> {difference.published}</p>}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                      <GlassButton variant="primary" icon={Save} loading={savingPost} onClick={saveMetadata}>
                        Salvar dados de publicação
                      </GlassButton>
                    </div>
                  </div>
                </div>
              ) : postView === 'social' ? (
                <div className="h-full overflow-y-auto bg-slate-950 p-6">
                  <div className="mx-auto max-w-5xl space-y-5">
                    <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                      <div>
                        <h4 className="text-xl font-semibold text-white">Pacote de distribuição social</h4>
                        <p className="mt-1 text-sm text-white/50">
                          Copy, hashtags, formato, acessibilidade e instruções de produção. O pacote social tem aprovação própria e pode seguir sem aprovar o artigo.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-3 py-1 text-xs text-cyan-100">
                          {selectedPost.social_format || selectedPost.social_package?.strategy?.recommended_format || 'formato pendente'}
                        </span>
                        <span className={`rounded-full border px-3 py-1 text-xs ${selectedPost.social_review_status === 'approved' ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/25 bg-amber-500/10 text-amber-100'}`}>
                          {selectedPost.social_review_status === 'approved' ? 'Revisão social aprovada' : 'Aguardando pacote/revisão'}
                        </span>
                        {selectedPost.status !== 'discarded' && <GlassButton variant="secondary" loading={creatingSocialForArticleId === selectedPost?.id} onClick={generateSocialPackageFromArticle}>Preparar pacote completo</GlassButton>}
                      </div>
                    </div>

                    {!selectedPost.social_package && !selectedPost.instagram_caption && !selectedPost.tiktok_script ? (
                      <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-5 text-sm text-amber-100">
                        Este artigo ainda não recebeu a versão social 5.0.
                      </div>
                    ) : (
                      <>
                        <div className="grid gap-4 lg:grid-cols-2">
                          <SocialBlock title="Instagram" subtitle={selectedPost.social_package?.channels?.instagram?.format}>
                            {selectedPost.social_package?.channels?.instagram?.caption || selectedPost.instagram_caption || 'Sem legenda.'}
                            {'\n\n'}
                            {(selectedPost.social_package?.channels?.instagram?.hashtags || []).join(' ') || selectedPost.instagram_hashtags || ''}
                          </SocialBlock>
                          <SocialBlock title="TikTok" subtitle={selectedPost.social_package?.channels?.tiktok?.format}>
                            {selectedPost.social_package?.channels?.tiktok?.hook ? `Gancho: ${selectedPost.social_package.channels.tiktok.hook}\n\n` : ''}
                            {selectedPost.social_package?.channels?.tiktok?.caption || selectedPost.tiktok_script || 'Sem roteiro.'}
                          </SocialBlock>
                          <SocialBlock title="X" subtitle={selectedPost.social_package?.channels?.x?.format || selectedPost.x_content?.format}>
                            {(selectedPost.social_package?.channels?.x?.posts || selectedPost.x_content?.posts || []).map((text, index) => (
                              <div key={`${selectedPost.id}-x-${index}`} className="mb-3 rounded-lg border border-white/10 bg-slate-950/60 p-3">
                                <span className="mb-1 block text-xs text-white/35">Post {index + 1} · {String(text).length}/280</span>
                                {text}
                              </div>
                            ))}
                          </SocialBlock>
                          <SocialBlock title="Facebook e canais complementares">
                            {selectedPost.social_package?.complementary_channels?.facebook?.copy || selectedPost.facebook_post || 'Consulte as decisões e justificativas no pacote completo abaixo.'}
                          </SocialBlock>
                        </div>

                        <SocialBlock title="Estratégia e decisão de mídia">
                          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-white/70">
                            {JSON.stringify({
                              strategy: selectedPost.social_package?.strategy,
                              complementary_channels: selectedPost.social_package?.complementary_channels,
                              publishing: selectedPost.social_package?.publishing,
                            }, null, 2)}
                          </pre>
                        </SocialBlock>

                        <SocialBlock title="Prompts e instruções de produção" subtitle="Imagem, carrossel ou vídeo de 30 segundos">
                          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-white/70">
                            {JSON.stringify(selectedPost.social_package?.creative || {}, null, 2)}
                          </pre>
                        </SocialBlock>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full overflow-y-auto bg-slate-950 p-6">
                  <div className="mx-auto max-w-3xl">
                    <h4 className="text-xl font-semibold text-white">Solicitar alteração ao Hub</h4>
                    <p className="mt-2 text-sm text-white/50">
                      Escreva normalmente o que deseja mudar. A nova versão ficará como “needs_review” e deverá ser revisada antes de aprovar.
                    </p>
                    <textarea
                      value={revisionRequest}
                      onChange={(event) => setRevisionRequest(event.target.value)}
                      placeholder="Ex.: Reescreva o resumo executivo com linguagem mais natural e retire referências ao processo de correção."
                      className="mt-5 min-h-48 w-full rounded-xl border border-white/10 bg-white/[0.05] p-4 text-white outline-none focus:border-cyan-300/50"
                    />
                    <div className="mt-4 flex justify-end">
                      <GlassButton variant="primary" loading={revisingPost} onClick={requestRevision}>
                        Aplicar alteração
                      </GlassButton>
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-white/10 bg-slate-950 p-4">
              <p className={`text-xs ${postHasUnsavedChanges ? 'text-amber-200' : 'text-white/40'}`}>{postHasUnsavedChanges ? 'Alterações locais ainda não foram enviadas.' : 'Todas as alterações estão salvas.'}</p>
              <div className="flex flex-wrap gap-2">
                <GlassButton variant="secondary" onClick={closePost}>Fechar</GlassButton>
                <GlassButton variant="secondary" icon={Save} loading={savingPost} onClick={() => updatePost({ ...postMetadata, blog_html: postHtml })}>Salvar</GlassButton>
                <GlassButton variant="primary" icon={CheckCircle} loading={savingPost} onClick={() => updatePost({ ...postMetadata, blog_html: postHtml, status: 'approved' }, true)}>Aprovar</GlassButton>
                <GlassButton variant="danger" loading={savingPost} onClick={() => updatePost({ status: 'discarded' }, true)}>Descartar</GlassButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderContent = () => {
    if (activeTab === 'social') return <SocialApproval onDirtyChange={setSocialDirty} initialPackageId={socialPackageToOpen} onInitialPackageOpened={() => { setSocialPackageToOpen(null); const next = new URL(window.location.href); next.searchParams.delete('package'); window.history.replaceState({}, '', next); }} />;
    if (activeTab === 'campanhas') return renderCampaigns();
    if (activeTab === 'atencao') return renderAttention();
    if (activeTab === 'newsletter') return renderNewsletter();
    if (activeTab === 'leads') return renderLeads();
    if (activeTab === 'conectores') return renderConnectors();
    if (activeTab === 'credenciais') return renderCredentials();
    if (activeTab === 'blog') return renderBlog();
    return renderBI();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Marketing</h1>
          <p className="text-white/50">
            BI do grupo para Imobiliária, Construtora, canais pagos, RD Station, portais e WhatsApp.
          </p>
          {periodo && (
            <p className="mt-1 text-xs text-white/35">
              Período: {new Date(`${periodo.data_inicio}T00:00:00`).toLocaleDateString('pt-BR')} até {new Date(`${periodo.data_fim}T00:00:00`).toLocaleDateString('pt-BR')}
            </p>
          )}
        </div>
        <GlassButton variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>
          Atualizar
        </GlassButton>
      </div>

      {socialSuccess && (
        <GlassCard className="border-emerald-400/30 bg-emerald-500/10 p-4" hover={false}>
          <p className="text-sm text-emerald-100">{socialSuccess}</p>
        </GlassCard>
      )}

      {error && (
        <GlassCard className="border-red-400/30 bg-red-500/10 p-4" hover={false}>
          <p className="text-sm text-red-100">{error}</p>
        </GlassCard>
      )}

      <GlassCard className="p-2" hover={false}>
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { if (activeTab !== 'social' || !socialDirty || window.confirm('Descartar alterações não salvas da aprovação social?')) { setSocialDirty(false); setActiveTab(tab.id); } }}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white/10 text-white'
                    : 'text-white/55 hover:bg-white/[0.04] hover:text-white/85'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </GlassCard>

      <FadeIn>{renderContent()}</FadeIn>
    </div>
  );
};

export default Marketing;
